import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LiksPage } from '../../components/liks/LiksPage/LiksPage';
import { LiksPageHeading } from '../../components/liks/LiksPageHeading/LiksPageHeading';
import { canManageChatbotTraining } from '../../features/chatbots/chatbotPermissions';
import { sortChatbotMessagesAscending } from '../../features/chatbots/chatbotConversationMessages';
import {
  canShowTrainingImproveAction,
  resolveTrainingPairFromMessage,
} from '../../features/chatbots/chatbotConversationTrainingPair';
import { resolveChatbotTabPath } from '../../features/chatbots/chatbotTabs';
import { useCurrentChatbotRole } from '../../features/chatbots/hooks/useCurrentChatbotRole';
import { useCreateChatbotTrainingExampleMutation } from '../../features/chatbots/mutations/useChatbotTrainingExampleMutations';
import { useChatbotConversationMessagesInfiniteQuery } from '../../features/chatbots/queries/useChatbotConversationMessagesInfiniteQuery';
import { useChatbotConversationQuery } from '../../features/chatbots/queries/useChatbotConversationQuery';
import { useChatbotQuery } from '../../features/chatbots/queries/useChatbotQuery';
import { useKnowledgeDatabasesQuery } from '../../features/knowledge/queries/useKnowledgeDatabasesQuery';
import { useAutohideScrollbar } from '../../hooks/useAutohideScrollbar';
import { useLoadOlderChatMessages } from '../../hooks/useLoadOlderChatMessages';
import { useScrollToBottom } from '../../hooks/useScrollToBottom';
import { useI18n } from '../../i18n/useI18n';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import type { ChatMessage, ChatMessageSource } from '../../types/chatMessage';
import '../platformChat/PlatformChatPage.css';
import { ChatbotConversationMessageListHeader } from './ChatbotConversationMessageListHeader';
import { ChatbotConversationMessagesStatus } from './ChatbotConversationMessagesStatus';
import { renderChatbotConversationPageGate } from './renderChatbotConversationPageGate';
import { ChatbotConversationTrainingDialog } from './ChatbotConversationTrainingDialog';
import { ChatbotConversationTrainingSourceDialog } from './ChatbotConversationTrainingSourceDialog';
import { ChatbotConversationTranscript } from './ChatbotConversationTranscript';
import { mapChatbotConversationMessages } from './chatbotConversationMessageMapping';
import './ChatbotConversationPage.css';

type TrainingDialogState = {
  messageId: string;
  question: string;
  answer: string;
};

type TrainingSourceDialogState = {
  trainingExampleId: string;
  sourceTitle: string;
  isDeleted: boolean;
};

export function ChatbotConversationPage() {
  const { chatbotId, conversationId } = useParams<{
    chatbotId: string;
    conversationId: string;
  }>();
  const { translate } = useI18n();
  const knowledgeDatabasesQuery = useKnowledgeDatabasesQuery();
  const chatbotQuery = useChatbotQuery(chatbotId);
  const conversationQuery = useChatbotConversationQuery(
    chatbotId,
    conversationId,
  );
  const messagesQuery = useChatbotConversationMessagesInfiniteQuery(
    chatbotId,
    conversationId,
  );
  const currentChatbotRole = useCurrentChatbotRole(chatbotId);
  const canManageTraining = canManageChatbotTraining(currentChatbotRole);
  const createTrainingMutation = useCreateChatbotTrainingExampleMutation(
    chatbotId ?? '',
  );
  const [trainingDialogState, setTrainingDialogState] =
    useState<TrainingDialogState | null>(null);
  const [trainingSourceDialogState, setTrainingSourceDialogState] =
    useState<TrainingSourceDialogState | null>(null);
  const messagesScrollbar = useAutohideScrollbar();
  const {
    containerRef: messagesRef,
    isAtBottom,
    checkIsAtBottom,
    scrollToBottom,
  } = useScrollToBottom();
  const hasInitialScrollRef = useRef(false);
  const isLoadingOlderMessagesRef = useRef(false);

  const chatbotName = chatbotQuery.data?.name || translate('chatbots.title');
  const chatbotDisplayName =
    chatbotQuery.data?.displayName?.trim() ||
    chatbotQuery.data?.name ||
    translate('chatbots.title');
  const visitorName = translate(
    'chatbots.conversations.detail.anonymousVisitor',
  );

  const chatbotKnowledgeBaseId =
    chatbotQuery.data?.knowledgeBaseId?.trim() ?? '';
  const accessibleKnowledgeBases = useMemo(() => {
    if (knowledgeDatabasesQuery.data == null) {
      return undefined;
    }

    return knowledgeDatabasesQuery.data.map((knowledgeBase) => ({
      id: knowledgeBase.id,
      name: knowledgeBase.name,
    }));
  }, [knowledgeDatabasesQuery.data]);

  const messages = useMemo((): ChatMessage[] => {
    const rawMessages = messagesQuery.data?.pages.flatMap((page) => page) ?? [];
    const sorted = sortChatbotMessagesAscending(rawMessages);
    return mapChatbotConversationMessages({
      rawMessages: sorted,
      conversationId: conversationId ?? '',
      visitorName,
      chatbotDisplayName,
      chatbotKnowledgeBaseId,
    });
  }, [
    chatbotKnowledgeBaseId,
    chatbotDisplayName,
    conversationId,
    messagesQuery.data?.pages,
    visitorName,
  ]);

  const messageCount = messages.length;
  const hasNextPage = messagesQuery.hasNextPage ?? false;
  const isFetchingNextPage = messagesQuery.isFetchingNextPage;

  const { handleScrollNearTop } = useLoadOlderChatMessages({
    containerRef: messagesRef,
    hasNextPage,
    isFetchingNextPage,
    isLoadingOlderMessagesRef,
    fetchNextPage: messagesQuery.fetchNextPage,
    contentVersion: messageCount,
    isReady: !messagesQuery.isLoading && messageCount > 0,
  });

  const handleMessagesScroll = useCallback(() => {
    messagesScrollbar.onScroll();
    checkIsAtBottom();
    handleScrollNearTop();
  }, [checkIsAtBottom, handleScrollNearTop, messagesScrollbar]);

  const canImproveMessage = useCallback(
    (message: ChatMessage) =>
      canShowTrainingImproveAction(message, canManageTraining),
    [canManageTraining],
  );

  const handleImproveMessage = useCallback(
    (messageId: string) => {
      const pair = resolveTrainingPairFromMessage(messages, messageId);
      setTrainingDialogState({
        messageId,
        question: pair.question,
        answer: pair.answer,
      });
    },
    [messages],
  );

  const handleOpenTrainingExample = useCallback((source: ChatMessageSource) => {
    const trainingExampleId = source.trainingExampleId?.trim() ?? '';
    if (trainingExampleId.length === 0) {
      return;
    }

    setTrainingSourceDialogState({
      trainingExampleId,
      sourceTitle: source.sourceTitle?.trim() ?? '',
      isDeleted: source.refStatus === 'TRAINING_EXAMPLE_DELETED',
    });
  }, []);

  const handleCreateTrainingExample = useCallback(
    (input: { question: string; answer: string; sortOrder: number | null }) => {
      if (!chatbotId?.trim()) {
        return;
      }

      createTrainingMutation.mutate(input, {
        onSuccess: () => {
          toast.success({
            title: translate('chatbots.training.createSuccessTitle'),
            description: '',
          });
          setTrainingDialogState(null);
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('chatbots.training.createErrorTitle'),
          );
        },
      });
    },
    [chatbotId, createTrainingMutation, translate],
  );

  useEffect(() => {
    hasInitialScrollRef.current = false;
    isLoadingOlderMessagesRef.current = false;
  }, [chatbotId, conversationId]);

  useEffect(() => {
    const frame = requestAnimationFrame(checkIsAtBottom);
    return () => cancelAnimationFrame(frame);
  }, [messageCount, checkIsAtBottom]);

  useEffect(() => {
    if (
      messagesQuery.isLoading ||
      messageCount === 0 ||
      hasInitialScrollRef.current
    ) {
      return;
    }

    scrollToBottom({ behavior: 'auto' });
    hasInitialScrollRef.current = true;
  }, [messageCount, messagesQuery.isLoading, scrollToBottom]);

  const messageListHeader = (
    <ChatbotConversationMessageListHeader
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      hasMessages={messages.length > 0}
    />
  );

  const gate = renderChatbotConversationPageGate({
    chatbotId,
    conversationId,
    isChatbotLoading: chatbotQuery.isLoading,
    isConversationLoading: conversationQuery.isLoading,
    isChatbotError: chatbotQuery.isError,
    chatbotError: chatbotQuery.error,
    hasChatbot: Boolean(chatbotQuery.data),
    isConversationError: conversationQuery.isError,
    hasConversation: Boolean(conversationQuery.data),
    translate,
  });

  if (gate != null) {
    return gate;
  }

  // Narrowed by ChatbotConversationPageGate above.
  const resolvedChatbotId = chatbotId!;
  const conversationsTabPath = resolveChatbotTabPath(
    resolvedChatbotId,
    'conversations',
  );

  return (
    <>
      <LiksPage
        size="big"
        className="platform-chat-page chatbot-conversation-page"
        header={
          <LiksPageHeading
            aria-label={translate('chatbots.breadcrumb.legend')}
            items={[
              { label: translate('chatbots.title'), to: '/chatbots' },
              {
                label: chatbotName,
                to: resolveChatbotTabPath(resolvedChatbotId, 'overview'),
              },
              {
                label: translate('chatbots.tabs.conversations'),
                to: conversationsTabPath,
              },
              { label: translate('chatbots.conversations.detail.title') },
            ]}
          />
        }
        content={
          messagesQuery.isLoading && messages.length === 0 ? (
            <ChatbotConversationMessagesStatus kind="loading" />
          ) : messagesQuery.isError && messages.length === 0 ? (
            <ChatbotConversationMessagesStatus kind="error" />
          ) : messages.length === 0 ? (
            <ChatbotConversationMessagesStatus kind="empty" />
          ) : (
            <ChatbotConversationTranscript
              messages={messages}
              messagesRef={messagesRef}
              messagesScrollbarClassName={messagesScrollbar.className}
              onMessagesScroll={handleMessagesScroll}
              messageListHeader={messageListHeader}
              isAtBottom={isAtBottom}
              onScrollToBottom={() => scrollToBottom()}
              chatbotKnowledgeBaseId={chatbotKnowledgeBaseId}
              accessibleKnowledgeBases={accessibleKnowledgeBases}
              canImproveMessage={canImproveMessage}
              onImproveMessage={handleImproveMessage}
              onOpenTrainingExample={handleOpenTrainingExample}
            />
          )
        }
      />
      {trainingDialogState != null && (
        <ChatbotConversationTrainingDialog
          key={trainingDialogState.messageId}
          question={trainingDialogState.question}
          answer={trainingDialogState.answer}
          isSubmitting={createTrainingMutation.isPending}
          onCreate={handleCreateTrainingExample}
          onClose={() => setTrainingDialogState(null)}
        />
      )}
      {trainingSourceDialogState != null && (
        <ChatbotConversationTrainingSourceDialog
          key={trainingSourceDialogState.trainingExampleId}
          chatbotId={resolvedChatbotId}
          trainingExampleId={trainingSourceDialogState.trainingExampleId}
          sourceTitle={trainingSourceDialogState.sourceTitle}
          isDeleted={trainingSourceDialogState.isDeleted}
          canEdit={canManageTraining}
          onClose={() => setTrainingSourceDialogState(null)}
        />
      )}
    </>
  );
}
